import React from "react";
import { useHistory } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  Avatar,
  IconButton,
} from "@mui/material";
import {
  Close as CloseIcon,
  CreditCard as CreditCardIcon,
  AccountBalanceWallet as WalletIcon,
  Subscriptions as SubscriptionsIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import {
  blue,
  green,
  red,
  grey,
  amber,
} from "@mui/material/colors";
import { ADMIN_ROUTES } from "../../routes/Admin/AdminPaths";

const PaymentDetailModal = ({ open, payment, onClose }) => {
  const history = useHistory();

  const handleClose = () => {
    onClose();
  };

  const handleFullPreview = () => {
    handleClose();
    history.push(ADMIN_ROUTES.paymentDetail.replace(":id", payment.id));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return "Invalid date";
    }
  };

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "success";
      case "pending":
        return "warning";
      case "failed":
        return "error";
      case "refunded":
        return "info";
      case "canceled":
        return "default";
      default:
        return "default";
    }
  };

  const getPaymentTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case "wallet_funding":
        return <WalletIcon fontSize="small" />;
      case "subscription_new":
      case "subscription_renewal":
      case "subscription_change":
        return <SubscriptionsIcon fontSize="small" />;
      default:
        return <CreditCardIcon fontSize="small" />;
    }
  };

  const getPaymentTypeLabel = (type) => {
    switch (type?.toLowerCase()) {
      case "wallet_funding":
        return "Wallet Funding";
      case "subscription_new":
        return "New Subscription";
      case "subscription_renewal":
        return "Subscription Renewal";
      case "subscription_change":
        return "Plan Change";
      default:
        return type || "Unknown";
    }
  };

  const getProviderIcon = (provider) => {
    switch (provider?.toLowerCase()) {
      case "stripe":
        return "💳";
      case "binance":
        return "🪙";
      default:
        return "💰";
    }
  };

  if (!payment) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Quick Payment Info
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ py: 2 }}>
          {/* Payment Avatar and Basic Info */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 3,
              p: 2.5,
              borderRadius: 2,
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "grey.200",
            }}
          >
            <Avatar
              sx={{
                bgcolor:
                  getStatusColor(payment.status) === "success"
                    ? green[500]
                    : getStatusColor(payment.status) === "error"
                    ? red[500]
                    : getStatusColor(payment.status) === "warning"
                    ? amber[500]
                    : blue[500],
                width: 72,
                height: 72,
                border: "3px solid",
                borderColor: "background.paper",
                boxShadow: 2,
              }}
            >
              {getPaymentTypeIcon(payment.payment_type)}
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                {formatCurrency(payment.amount, payment.currency)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {payment.username || "Unknown User"}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Chip
                  label={payment.status?.toUpperCase() || "UNKNOWN"}
                  size="small"
                  color={getStatusColor(payment.status)}
                />
                <Chip
                  icon={getPaymentTypeIcon(payment.payment_type)}
                  label={getPaymentTypeLabel(payment.payment_type)}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Box>
          </Box>

          {/* Essential Fields */}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Payment ID
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontFamily: "monospace", fontSize: "0.8rem", wordBreak: "break-all", mt: 0.5 }}
              >
                {payment.id}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Provider
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                <span>{getProviderIcon(payment.payment_provider)}</span>
                <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
                  {payment.payment_provider || "Unknown"}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Date
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, fontSize: "0.85rem" }}>
                {formatDate(payment.created_at)}
              </Typography>
            </Grid>

            {payment.coins_purchased && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Coins Purchased
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: amber[600], mt: 0.5 }}>
                  🪙 {payment.coins_purchased?.toLocaleString()}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          onClick={handleFullPreview}
          startIcon={<VisibilityIcon />}
          variant="outlined"
          fullWidth
        >
          View Full Payment
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentDetailModal;
